import React, { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ModelListPage, ModelListPageStyled } from '/imports/ui/ModelListPage';
import { modelClass, consumptionMeterType, pullUtilityBills, compileOneYearConsumption } from '/imports/api/consumptionMeters';
import { formatDateTime, formatNumber } from '/imports/utils';
import { strings } from '/imports/strings';
import { useHasConsumptionMeters, useUser } from '/imports/hooks';
import { PageHeading } from '/imports/ui/PageHeading';
import { Button } from '/imports/ui/Button';
import { ButtonDropdown } from '/imports/ui/ButtonDropdown';
import { Icon } from '/imports/ui/Icon';
import { PullConsumptionIntervalsModal } from '/imports/ui/PullConsumptionIntervalsModal';
import { ExportConsumptionIntervalsModal } from '/imports/ui/ExportConsumptionIntervalsModal';
import { SendToProjectModal } from '/imports/ui/SendToProjectModal';
import { modelClass as utilityAccountModelClass, processSdgeIntervals as processSdgeIntervalsMethod, processSdgeBills as processSdgeBillsMethod } from '/imports/api/utilityAccounts';
import { toastOnError } from '/imports/ui/utils';
import { RichSelect } from '/imports/ui/RichSelect';
import { cacheAggregateConsumptionIntervals } from '/imports/api/consumptionIntervals';
import { MILLIS_1_YEAR } from '/imports/constants';

const ConsumptionMetersPageStyled = styled(ModelListPageStyled)`
  .filters {
    .options {
      z-index: 10999992
    }
  }
`;

/**
 * List page for consumption meters with tools to pull, process, export, and compile consumption data.
 */
export const ConsumptionMetersPage = () => {
  const canReadMeters = useHasConsumptionMeters();
  const user = useUser();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showPullModal, setShowPullModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showSendToProjectModal, setShowSendToProjectModal] = useState(false);
  const [utilityAccountId, setUtilityAccountId] = useState<string>();
  /** Triggers processing of stored SDG&E interval data. */
  const processSdgeIntervals = toastOnError(async () => {
    const toastId = toast.info(strings.Running(), { autoClose: Number.MAX_SAFE_INTEGER });
    await processSdgeIntervalsMethod.call();
    toast.dismiss(toastId);
    toast.success(strings.Done());
  });
  /** Triggers processing of stored SDG&E bills. */
  const processSdgeBills = toastOnError(async () => {
    const toastId = toast.info(strings.Running(), { autoClose: Number.MAX_SAFE_INTEGER });
    await processSdgeBillsMethod.call();
    toast.dismiss(toastId);
    toast.success(strings.Done());
  });
  /** Pulls utility bills for the selected meters. */
  const pullBills = toastOnError(async () => {
    if (!selectedIds.length) return;
    const toastId = toast.info(strings.Running(), { autoClose: Number.MAX_SAFE_INTEGER });
    await pullUtilityBills.call({ meterIds: selectedIds });
    toast.dismiss(toastId);
    toast.success(strings.Done());
  });
  /** Prompts for a start date and caches aggregate consumption intervals from then. */
  const cacheConsumption = toastOnError(async () => {
    const startDate = prompt(strings.EnterTheStartDate(), (new Date(Date.now() - 2 * MILLIS_1_YEAR)).toISOString().split('T').at(0));
    const meterIds = selectedIds.length ? selectedIds : undefined;
    if (!startDate) return;
    await cacheAggregateConsumptionIntervals.call({ meterIds, startDate });
    toast.success(strings.Running());
  });
  /** Compiles 12-month usage totals across meters and reports the results. */
  const runCompileOneYearConsumption = toastOnError(async () => {
    const toastId = toast.info(strings.Running(), { autoClose: Number.MAX_SAFE_INTEGER });
    const result = await compileOneYearConsumption.call(undefined);
    toast.dismiss(toastId);
    toast.success(`${strings.Done()} — ${result.succeeded}/${result.total} updated (${result.skipped} insufficient data)`);
  });
  return <ModelListPage
    permission={() => canReadMeters}
    key={`${utilityAccountId}`}
    styled={ConsumptionMetersPageStyled}
    modelClass={modelClass}
    header={(
      <PageHeading
        left={<h1>{strings.ConsumptionMeters()}</h1>}
        right={
          <div className="buttons">
            {
              selectedIds.length ?
                <>
                  <Button onClick={() => setShowPullModal(true)}>{strings.PullConsumption()} ({selectedIds.length})</Button>
                  <Button onClick={() => setShowSendToProjectModal(true)}>{strings.SendToProject()} ({selectedIds.length})</Button>
                  <Button onClick={() => pullBills()}>{strings.PullBills()} ({selectedIds.length})</Button>
                </>
                : null
            }
            <ButtonDropdown
              label={<>{strings.Tools()}<Icon icon="keyboardArrowDown" /></>}
              items={[
                { key: 'exportConsumption', label: strings.ExportConsumption(), onClick: () => selectedIds.length ? setShowExportModal(true) : toast.info(strings.PleaseSelectMetersToExport()) },
                { key: 'processSdgeIntervals', label: strings.ProcessSdgeIntervals(), onClick: () => processSdgeIntervals() },
                { key: 'processSdgeBills', label: strings.ProcessSdgeBills(), onClick: () => processSdgeBills() },
                { key: 'compileOneYearConsumption', label: strings.Compile12MonthUsage(), onClick: () => runCompileOneYearConsumption() },
                { key: 'cache', label: strings.CacheConsumption(), onClick: () => cacheConsumption() },
              ]}
            />
            <>
              {
                showPullModal && selectedIds.length ?
                  <PullConsumptionIntervalsModal meterIds={selectedIds} onClose={() => setShowPullModal(false)} />
                  :
                  null
              }
              {
                showExportModal && selectedIds.length ?
                  <ExportConsumptionIntervalsModal meterIds={selectedIds} onClose={() => setShowExportModal(false)} />
                  :
                  null
              }
              {
                user?.isSuperUser && showSendToProjectModal && selectedIds.length ?
                  <SendToProjectModal meterIds={selectedIds} onClose={() => setShowSendToProjectModal(false)} />
                  :
                  null
              }
            </>
          </div>
        }
      />
    )}
    columns={[
      { field: 'name', render: (doc) => <Link to={`${modelClass.pathPrefix}/${doc._id}`}>{doc.name}</Link> },
      { field: 'identifier' },
      { field: 'accountNumber' },
      { field: 'type', render: (doc) => <span>{consumptionMeterType(doc)}</span> },
      { field: 'oneYearConsumption', label: strings.TwelveMonthUsage(), render: (doc) => <span>{typeof doc.oneYearConsumption === 'number' ? formatNumber(doc.oneYearConsumption, 0) : '—'}</span> },
      { field: 'expiresAt', label: strings.Expires(), render: (doc) => <span>{doc.expiresAt || strings.Never()}</span> },
      { field: 'createdAt', render: (doc) => <span>{doc.createdAt && formatDateTime(doc.createdAt)}</span> },
    ]}
    query={utilityAccountId ? { utilityAccountId } as any : {}}
    defaultSort={{ createdAt: -1 }}
    onSelectedIds={(selectedIds) => setSelectedIds(selectedIds)}
    extraFilters={() => (
      <div>
        <RichSelect
          placeholder={strings.UtilityAccount()}
          value={utilityAccountId}
          listMethod={utilityAccountModelClass.listMethod}
          listQuery={{ active: true }}
          mapToValue={(ua) => ({ value: ua._id, label: ua.name || ua.username || ua._id })}
          onChange={(option) => setUtilityAccountId(option.value)}
          clearable
          onClear={() => setUtilityAccountId(undefined)}
        />
      </div>
    )}
  />;
};